/**
 * LOADING A REMOTE READ, WITH THE THREE STATES AN OWNER ACTUALLY NEEDS.
 *
 * `loading` / `ready` / `failed` is not enough on its own, and this product is
 * the reason why: "failed" collapses "Scout is not reachable", "you are not
 * signed in" and "Scout refused this" into one word, and those need three
 * different things done about them. So the failure carries the backend's own
 * error through untouched — name, message and status — and the view decides how
 * to say it.
 *
 * THERE IS NO CLIENT CACHE OF RECORD. Every read is a read of canonical Scout
 * state; a value held here lives for as long as its view is mounted and no
 * longer. A cache that outlived a reload would be a second source of truth
 * about a research record, which is the one thing this workspace must not be.
 */

import { signal, type Signal } from '@preact/signals';
import { readGateway, type GatewayError, type GatewayResult } from './gateway';

export interface Resource<T> {
	status: 'loading' | 'ready' | 'failed';
	data: T | null;
	error: GatewayError | null;
	/** When the answer arrived, for the "as of" line every view carries — so a
	 *  stale panel says WHEN it was true rather than implying it still is. */
	readAt: string | null;
}

export interface Loadable<T> {
	state: Signal<Resource<T>>;
	load(): Promise<void>;
}

/**
 * WHETHER A PAYLOAD IS THE SHAPE THIS VIEW DECLARED, ASKED AT THE DOOR.
 *
 * WHY THIS EXISTS, MEASURED. `readGateway` proves the envelope — `success:
 * true` and a `data` member — and says nothing at all about what is IN `data`,
 * because it cannot: the route is named by the caller and the payload belongs
 * to the view. Every view then dereferences fields off `data` on the strength
 * of a TypeScript type, which is a claim about the code and not about the
 * answer. When the answer is not that shape, the field is `undefined` and the
 * dereference is a TypeError inside `render` — and a throw inside a Preact
 * render does not degrade one panel, it takes the whole workspace down. It was
 * observed doing exactly that, from the default tier's own run: an answer the
 * science views could not read produced an unhandled exception attributed to
 * whichever test happened to be running.
 *
 * THE FAILURE IS THE HONEST ANSWER, NOT A CRASH. An answer this build cannot
 * read is a real thing an Owner should be told: the deployment answered, and
 * the answer is not the one this view was written against — which usually means
 * a Worker and a browser bundle from different revisions. That is a FAILURE
 * with a name and a sentence, and it renders in the same panel as every other
 * failure, rather than a blank page.
 *
 * IT IS A GUARD, NOT A SCHEMA. The caller names the members it is about to
 * read and no more; nothing here infers structure, coerces values or repairs a
 * payload. A field this guard admits is a field the view really reads, and a
 * rejection is a sentence naming the member that was missing.
 */
export function createLoadable<T>(
	loader: () => Promise<GatewayResult<T>>,
	/** Answers `null` when the payload is the declared shape, or a sentence
	 *  naming what is wrong with it. Absent means this read dereferences nothing
	 *  and any payload will do. */
	expects?: (payload: unknown) => string | null,
): Loadable<T> {
	const state = signal<Resource<T>>({
		status: 'loading',
		data: null,
		error: null,
		readAt: null,
	});
	let generation = 0;
	return {
		state,
		async load(): Promise<void> {
			// A LATER LOAD WINS. Without this, a slow first read that resolves
			// after a fast second one would overwrite the newer answer with the
			// older — which is the shape of bug that shows an Owner yesterday's
			// research state while the record has already moved.
			const mine = ++generation;
			state.value = { status: 'loading', data: null, error: state.value.error, readAt: state.value.readAt };
			const result = await loader();
			if (mine !== generation) return;
			if (!result.ok) {
				state.value = { status: 'failed', data: null, error: result.error, readAt: null };
				return;
			}
			// THE SHAPE IS CHECKED ONCE, HERE, AND A MISMATCH NEVER REACHES A
			// VIEW. It is a `failed` state like any other, so the panel that
			// already exists for a refusal renders it, and the workspace stays
			// up.
			const complaint = expects === undefined ? null : expects(result.data);
			if (complaint !== null) {
				state.value = {
					status: 'failed',
					data: null,
					error: {
						name: 'ScoutAnswerNotUnderstood',
						message: `Scout answered, and the answer is not the shape this view reads — ${complaint}. This usually means the Worker and this page are from different revisions.`,
						status: 0,
					},
					readAt: null,
				};
				return;
			}
			state.value = {
				status: 'ready',
				data: result.data,
				error: null,
				readAt: new Date().toISOString(),
			};
		},
	};
}

/**
 * THE MEMBERS A VIEW IS ABOUT TO READ, NAMED BY THE VIEW.
 *
 * Why a list of names rather than a validator each call site writes: the check
 * has one shape — "is this a non-null object, and does it carry these members"
 * — and six copies of it would drift. What a view knows and this does not is
 * WHICH members it reads, so that is the only thing it supplies.
 */
export function expectsMembers(...members: readonly string[]): (payload: unknown) => string | null {
	return (payload: unknown): string | null => {
		if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
			return `the answer is not an object`;
		}
		const record = payload as Record<string, unknown>;
		const missing = members.filter((member) => record[member] === undefined);
		if (missing.length === 0) return null;
		return `it does not carry ${missing.map((member) => `'${member}'`).join(', ')}`;
	};
}

/** A read that needs no state of its own — for a value a view wants once. */
export async function readOnce<T>(path: string): Promise<GatewayResult<T>> {
	return await readGateway<T>(path);
}
